import { Download } from 'lucide-react';

import { KubesoloEnvironmentSettings, KubesoloTest } from '@api/types.gen';

import { EnvironmentId } from '@/react/portainer/environments/types';
import { pluralize } from '@/portainer/helpers/strings';
import { grammaticallyJoin } from '@/react/common/string-utils';
import { notifySuccess } from '@/portainer/services/notifications';

import { LoadingButton } from '@@/buttons';
import { confirm } from '@@/modals/confirm';
import { buildConfirmButton } from '@@/modals/utils';
import { TextTip } from '@@/Tip/TextTip';

import { useInstallMutation } from '../queries/useInstallMutation';

interface Props {
  test: KubesoloTest;
  environmentSettings: KubesoloEnvironmentSettings;
  /** Fields the test checked that the form has changed since, such as "Portainer API server URL" */
  changedSinceTest: Array<string>;
  onInstalled(environmentIds: Array<EnvironmentId>): void;
}

/** Installs the machines that passed, each named after its hostname, then clears the test. */
export function InstallButton({
  test,
  environmentSettings,
  changedSinceTest,
  onInstalled,
}: Props) {
  const installMutation = useInstallMutation();
  const { passed } = test.counts;

  if (passed === 0) {
    return null;
  }

  return (
    <div className="form-group">
      <div className="col-sm-12">
        <LoadingButton
          color="primary"
          icon={Download}
          isLoading={installMutation.isLoading}
          loadingText="Installing..."
          disabled={changedSinceTest.length > 0}
          onClick={handleClick}
          type="button"
          data-cy="kubesolo-install-button"
        >
          Install {passed} passed
        </LoadingButton>
        {changedSinceTest.length > 0 && (
          <TextTip color="orange" className="mt-2" inline={false}>
            The {grammaticallyJoin(changedSinceTest)} changed since the
            connection test. Test connections again to install with{' '}
            {changedSinceTest.length > 1 ? 'them' : 'it'}.
          </TextTip>
        )}
      </div>
    </div>
  );

  async function handleClick() {
    const machines = `${passed} ${pluralize(passed, 'machine')}`;
    const others = test.hosts.length - passed;
    const passedMachines =
      passed > 1 ? `Each of the ${machines}` : 'The machine';
    const confirmed = await confirm({
      title: 'Install KubeSolo',
      message: `${passedMachines} that passed becomes a KubeSolo Edge environment, named after its hostname. The connection test is then cleared${others > 0 ? `, with the results of the ${others} other ${pluralize(others, 'machine')}` : ''}.`,
      confirmButton: buildConfirmButton(
        `Install ${machines}`,
        'primary',
        0,
        'kubesolo-install-confirm-button'
      ),
    });

    if (!confirmed) {
      return;
    }

    installMutation.mutate(
      {
        testID: test.id,
        hosts: test.hosts
          .filter((host) => host.state === 'passed')
          .map(({ id }) => ({ id })),
        environmentSettings,
      },
      {
        onSuccess: (environmentIds) => {
          notifySuccess('Success', `Install started on ${machines}`);
          onInstalled(environmentIds);
        },
      }
    );
  }
}
